import type { PublicClientApplication } from '@azure/msal-browser';
import { Box, Text } from '@lego/klik-ui';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { Announcements } from '@/components/announcements';
import { ChatWidget } from '@/components/assistant';
import { NavigationBar } from '@/components/common';
import {
  ApplicationPage,
  ApplicationVersionPage,
  DeploymentPage,
  DeviceActionsPage,
  DevicePage,
  DevicesPage,
  DeviceTypesPage,
  FleetPage,
  ImageTagDetailPage,
  KpiDetailsPage,
  LandingPage,
  NotFoundPage,
  RepositoryPage,
  StoreServicesPage,
  UserProfilePage,
  WorkspacePage,
  WorkspacesPage,
} from '@/pages';
import { ErrorBoundary } from './components/error/ErrorBoundary';
import { useCacheProvider } from './hooks/swr/cache_provider';
import { DeviceActionsClonePage } from './pages/DeviceActionsClone';
import { DeviceActionsFleetAssignmentPage } from './pages/DeviceActionsFleetAssignment';
import { EmmaOsLicensesPage } from './pages/EmmaOsLicenses';
import { OsInstallersPage } from './pages/OsInstallers';
import { AuthProvider } from './providers/AuthProvider';
import { PageContextProvider } from './providers/PageContextProvider';
import { emmaTeamsChannelUrl } from './utility/external_urls';
import { paths } from './utility/global_paths';

export default function App({ msalInstance }: { msalInstance: PublicClientApplication }) {
  const provider = useCacheProvider();

  return (
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <AuthProvider msalInstance={msalInstance}>
        <SWRConfig
          value={{
            provider,
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            dedupingInterval: 2000,
          }}
        >
          <PageContextProvider>
            <Box background="#D9D9D9" minHeight="100vh">
              <NavigationBar />
              <Announcements />
              <ChatWidget />
              <Box paddingX={[0, 4, 6, 8]} paddingY={[4, 6, 8]} width="100%">
                <ErrorBoundary
                  fallback={
                    <Text>
                      Something went wrong!{' '}
                      <a href={emmaTeamsChannelUrl}>Please notify the EMMA team.</a>
                    </Text>
                  }
                >
                  <Routes>
                    <Route element={<LandingPage />} path="/" />
                    <Route element={<WorkspacesPage />} path="workspaces" />
                    <Route element={<WorkspacePage />} path="workspaces/:workspaceId/*" />
                    <Route element={<FleetPage />} path="workspaces/:workspaceId/fleets/:fleetId" />
                    <Route
                      element={<RepositoryPage />}
                      path="workspaces/:workspaceId/repositories/:repositoryId"
                    />
                    <Route
                      element={<ImageTagDetailPage />}
                      path="workspaces/:workspaceId/repositories/:repositoryId/tags/:imageTag"
                    />
                    <Route
                      element={<DeploymentPage />}
                      path="workspaces/:workspaceId/fleets/:fleetId/deployments/:deploymentId"
                    />
                    <Route
                      element={<ApplicationPage />}
                      path="workspaces/:workspaceId/applications/:applicationId"
                    />
                    <Route
                      element={<ApplicationVersionPage />}
                      path="workspaces/:workspaceId/applications/:applicationId/versions/:applicationVersionId"
                    />
                    <Route element={<DevicesPage />} path="devices" />
                    <Route
                      element={<DeviceActionsFleetAssignmentPage />}
                      path="devices/:deviceId/actions/fleet-assignment"
                    />
                    <Route
                      element={<DeviceActionsClonePage />}
                      path="devices/:deviceId/actions/clone"
                    />
                    <Route element={<DevicePage />} path="devices/:deviceId/*" />
                    <Route element={<DeviceActionsPage />} path="device-actions/*" />
                    <Route element={<StoreServicesPage />} path="store" />
                    <Route element={<StoreServicesPage />} path="store/:serviceName" />
                    <Route element={<KpiDetailsPage />} path="kpis" />
                    <Route element={<EmmaOsLicensesPage />} path={paths.emmaOsLicenses} />
                    <Route element={<OsInstallersPage />} path={paths.osDownloads} />
                    <Route element={<DeviceTypesPage />} path={paths.deviceTypes} />
                    <Route element={<UserProfilePage />} path="users/:oid" />
                    <Route element={<NotFoundPage />} path="*" />
                  </Routes>
                </ErrorBoundary>
              </Box>
            </Box>
          </PageContextProvider>
        </SWRConfig>
      </AuthProvider>
    </BrowserRouter>
  );
}
